"use client"

import Link from "next/link"
import { motion, useReducedMotion } from "framer-motion"
import { EventCard } from "@/components/event-card"
import { Button } from "@/components/ui/button"
import { MarketingCreateEventCta } from "@/components/marketing/marketing-create-event-cta"
import { getMarketingDemoEventCardProps } from "@/lib/demo/public-demo-event"
import {
  LANDING_HERO_HEADER_BLEND_STYLE,
  LANDING_HERO_IMAGE_OVERLAY_STYLE,
  LANDING_HERO_IMAGE_SOURCES,
  LANDING_HERO_PICTURE_IMG_SRC,
  LANDING_MEMORY_STRIP,
} from "@/lib/marketing/landing-media"
import Image from "next/image"
import {
  IMAGE_QUALITY_MARKETING_GRID,
} from "@/lib/media/image-presets"

const easeOut = [0.22, 1, 0.36, 1] as const

const demoCard = getMarketingDemoEventCardProps()

export function MarketingHero() {
  const reduceMotion = useReducedMotion()

  return (
    <section
      className="marketing-hero-immersive relative isolate w-full min-w-0 -mt-[var(--marketing-hero-header-underlap-mobile)] pt-[calc(var(--marketing-hero-header-underlap-mobile)+clamp(2rem,4.8vw,3.5rem))] pb-[clamp(2.75rem,6.8vw,4.75rem)] md:-mt-[var(--marketing-hero-header-underlap-desktop)] md:pt-[calc(var(--marketing-hero-header-underlap-desktop)+clamp(2rem,4.8vw,3.5rem))]"
    >
      <div
        className="pointer-events-none absolute left-1/2 top-0 z-0 h-[min(44rem,92svh)] w-screen max-w-none -translate-x-1/2 sm:h-[min(48rem,94svh)]"
        aria-hidden
      >
        <div className="relative h-full w-full">
          {/*
           * Native `<picture>` for AVIF/WebP + mobile/desktop art direction (`next/image` has no equivalent).
           * Wrapper + `max-w-none` / explicit height: Tailwind preflight sets `img { max-width:100%; height:auto }`,
           * which can fight `object-cover` in a full-bleed slot and read as vertical stretch on phones.
           */}
          <div className="absolute inset-0 overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <picture className="contents">
              <source
                media="(min-width: 768px)"
                srcSet={LANDING_HERO_IMAGE_SOURCES.desktop.avif}
                type="image/avif"
              />
              <source
                media="(min-width: 768px)"
                srcSet={LANDING_HERO_IMAGE_SOURCES.desktop.webp}
                type="image/webp"
              />
              <source srcSet={LANDING_HERO_IMAGE_SOURCES.mobile.avif} type="image/avif" />
              <source srcSet={LANDING_HERO_IMAGE_SOURCES.mobile.webp} type="image/webp" />
              <img
                src={LANDING_HERO_PICTURE_IMG_SRC}
                alt=""
                className="absolute inset-0 z-0 h-full w-full max-w-none object-cover object-center [min-height:100%] [min-width:100%]"
                sizes="100vw"
                fetchPriority="high"
                decoding="async"
              />
            </picture>
          </div>
          <div style={LANDING_HERO_HEADER_BLEND_STYLE} />
          <div style={LANDING_HERO_IMAGE_OVERLAY_STYLE} />
        </div>
      </div>

      <div className="relative z-[12] mx-auto max-w-[72rem] text-center">
        <motion.h1
          className="type-hero-title mx-auto max-w-[min(100%,30ch)] !mb-0 text-[clamp(2.5rem,6.9vw,5.15rem)] leading-[1.03] tracking-[-0.02em]"
          initial={reduceMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.75, ease: easeOut }}
        >
          <span className="block text-balance text-[color:var(--marketing-hero-text-primary)]">
            The threshold is crossed.
          </span>
          <span className="mt-1 block text-balance text-[color:var(--marketing-hero-text-secondary)] sm:mt-1.5">
            The memory remains.
          </span>
        </motion.h1>
        <motion.p
          className="type-hero-subtitle mx-auto mt-6 max-w-[min(100%,36rem)] text-balance text-[color:var(--marketing-hero-text-body)] sm:mt-8"
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reduceMotion ? 0 : 0.65,
            delay: reduceMotion ? 0 : 0.08,
            ease: easeOut,
          }}
        >
          A private place for invitations, photos, and memories—kept within your circle.
        </motion.p>

        <motion.div
          className="mx-auto mt-7 max-w-[72rem] sm:mt-8"
          initial={reduceMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reduceMotion ? 0 : 0.58,
            delay: reduceMotion ? 0 : 0.12,
            ease: easeOut,
          }}
        >
          <div
            className="mx-auto flex w-full max-w-md flex-col items-stretch justify-center gap-3 sm:max-w-none sm:flex-row sm:items-center sm:gap-4"
            role="group"
            aria-label="Get started"
          >
            <Button
              variant="inverse"
              size="lg"
              className="h-12 min-h-12 rounded-full border border-white/5 bg-zinc-100 px-8 text-zinc-950 shadow-none ring-1 ring-inset ring-white/10 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:bg-zinc-200 hover:opacity-100 sm:h-11 sm:min-h-0"
              asChild
            >
              <Link href="/demo">Open the living demo</Link>
            </Button>
            <MarketingCreateEventCta
              variant="ghost"
              className="h-12 min-h-12 text-[color:var(--marketing-hero-text-body)] hover:bg-white/10 hover:text-[color:var(--marketing-hero-text-primary)] sm:h-11 sm:min-h-0"
            />
          </div>
        </motion.div>
      </div>

      <motion.div
        className="relative z-[12] mx-auto mt-[clamp(1.75rem,5.5vw,3.75rem)] w-full max-w-[54rem] min-w-0 px-4 sm:px-0"
        initial={reduceMotion ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: reduceMotion ? 0 : 0.85,
          delay: reduceMotion ? 0 : 0.18,
          ease: easeOut,
        }}
      >
        <div className="relative">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: reduceMotion ? 0 : 0.8,
              delay: reduceMotion ? 0 : 0.22,
              ease: easeOut,
            }}
          >
            <EventCard
              {...demoCard}
                titleAs="h2"
              coverPriority
              surfaceClassName="border-white/5 shadow-none ring-1 ring-inset ring-white/10"
            />
          </motion.div>

          <motion.div
            className="mt-7 sm:mt-9"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: reduceMotion ? 0 : 0.55,
              delay: reduceMotion ? 0 : 0.32,
              ease: easeOut,
            }}
          >
            <p className="type-marketing-kicker mb-3.5 text-center sm:mb-4">
              The same night, remembered
            </p>
            <div className="flex flex-wrap justify-center gap-2.5 sm:flex-nowrap sm:gap-3.5">
              {LANDING_MEMORY_STRIP.map((photo, i) => (
                <motion.div
                  key={`landing-memory-strip-${i}`}
                  className="relative h-[3.75rem] w-[4.75rem] shrink-0 overflow-hidden rounded-lg border border-border/40 bg-card/35 shadow-sm shadow-black/25 sm:h-16 sm:w-[5.25rem] sm:rounded-xl"
                  initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: reduceMotion ? 0 : 0.45,
                    delay: reduceMotion ? 0 : 0.38 + i * 0.05,
                    ease: easeOut,
                  }}
                >
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    fill
                    sizes="(max-width: 640px) 4.75rem, 5.25rem"
                    quality={IMAGE_QUALITY_MARKETING_GRID}
                    className="object-cover"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  )
}
